import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import api from "../lib/api";
import InquiryForm from "../components/InquiryForm";
import HireInquiryForm from "../components/HireInquiryForm";
import SmartImage from "../components/SmartImage";
import SEO from "../lib/SEO";

const HIRE_CATEGORY = "Wedding Cake Hire";

export default function CakeDetail() {
    const { slug, category: categorySlug } = useParams();
    const [cake, setCake] = useState(null);
    const [notFound, setNotFound] = useState(false);

    // Back-link target: category portfolio if we came via /gallery/:category/:slug, else the gallery landing
    const backTo = categorySlug ? `/gallery/${categorySlug}` : "/gallery";
    const backLabel = categorySlug ? "Back to portfolio" : "Back to gallery";

    useEffect(() => {
        setNotFound(false);
        setCake(null);
        api.get(`/cakes/${slug}`)
            .then((r) => setCake(r.data))
            .catch(() => setNotFound(true));
    }, [slug]);

    if (notFound) {
        return (
            <main className="pt-32 pb-20 max-w-3xl mx-auto px-4 text-center" data-testid="cake-notfound">
                <h2 className="font-display text-3xl mb-4">Cake not found</h2>
                <Link to={backTo} className="btn-pink mt-4">{backLabel}</Link>
            </main>
        );
    }

    if (!cake) {
        return <main className="pt-32 pb-20 max-w-3xl mx-auto px-4 text-center text-ink-soft" data-testid="cake-loading">Loading…</main>;
    }

    return (
        <main data-testid="cake-detail-page">
            <SEO
                title={`${cake.name} — Cake Central`}
                description={cake.short_description}
                image={cake.image_url}
                path={`/gallery/${cake.slug}`}
                jsonLd={{
                    "@context": "https://schema.org",
                    "@type": "Product",
                    name: cake.name,
                    image: cake.image_url,
                    description: cake.short_description,
                    category: cake.category,
                    brand: { "@type": "Brand", name: "Cake Central" },
                }}
            />

            <section className="pt-24 md:pt-[10rem] pb-16 bg-[#FFF7FB]">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <Link to={backTo} className="inline-flex items-center gap-2 text-xs tracking-[0.2em] uppercase text-ink-soft hover:text-blush mb-8 transition-colors" data-testid="back-to-portfolio">
                        <ArrowLeft size={14} /> {backLabel}
                    </Link>

                    <div className="grid md:grid-cols-2 gap-10 md:gap-16">
                        <div>
                            <div className="aspect-[4/5] overflow-hidden">
                                <SmartImage src={cake.image_url} alt={cake.name} className="w-full h-full object-cover" />
                            </div>
                        </div>
                        {cake.category === HIRE_CATEGORY ? (
                            <div>
                                <h1 className="font-display font-bold text-4xl md:text-5xl text-ink mb-3 leading-none" data-testid="cake-title">
                                    {cake.name}
                                </h1>
                                {cake.price != null && (
                                    <p className="text-2xl text-blush font-medium mb-8" data-testid="cake-price">
                                        ${Number(cake.price).toFixed(0)}
                                    </p>
                                )}
                                <div className="h-px bg-[#EAE3DE] mb-8" />
                                <p className="text-xs tracking-[0.3em] uppercase text-ink-soft mb-4">Enquiry</p>
                                <HireInquiryForm cake={cake} />
                            </div>
                        ) : (
                            <div>
                                <p className="text-xs tracking-[0.3em] uppercase text-ink-soft mb-3">{cake.category}</p>
                                <h1 className="font-display text-4xl md:text-5xl text-ink mb-5 leading-none" data-testid="cake-title">
                                    {cake.name}
                                </h1>
                                <p className="text-ink-soft leading-relaxed mb-8 whitespace-pre-line">{cake.long_description}</p>
                                <div className="h-px bg-[#EAE3DE] mb-8" />
                                <p className="text-xs tracking-[0.3em] uppercase text-ink-soft mb-4">Enquire about this cake</p>
                                <InquiryForm cake={cake} compact />
                            </div>
                        )}
                    </div>
                </div>
            </section>
        </main>
    );
}
